import { Asset } from '@ynomia/core/dist/project/assets';
import { createSelector } from 'reselect';
import keyBy from 'lodash/keyBy';
import {
  getAssetManagerFilters,
  getAssetTypes,
  getFilteredAssets,
  getFilteredAssetsContext,
  getAssetManagerAssetSelection,
  getLayersKeyedByAncestorId,
  getLayersKeyedByParentID,
  getSelectedAssetType,
} from '..';
import map from '../map';
import {
  getLayerPathFromLayerID,
  getSelectedLayerAncestors,
  stringArrayMemoizeOptions,
} from '../../utils';

export const getFilteredAssetsForDigitalTwin = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      selectedLayerIds,
      tableFilter,
      openedLayerId,
      selectedBandIds,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;
    return getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      selectedLayerIds,
      tableFilter,
      openedLayerId,
      selectedBandIds,
      multiSearchModeFilter,
      fieldFilters,
    });
  },
);

const getFilteredAssetsForTwin = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
  ) => {
    const { openedLayerId } = assetManagerFilters;
    return getFilteredAssets(filteredAssetsContext, {
      openedLayerId,
    });
  },
);

export const getFilteredAssetKeyedByTwinId = createSelector(
  [
    getFilteredAssetsForDigitalTwin,
  ],
  filteredAssetsForDigitalTwin => new Map(filteredAssetsForDigitalTwin.map((asset) => {
    const [twinID] = asset.destination?.twinID || [];
    return [twinID, asset];
  })),
);

export const getFilteredTwinIds = createSelector(
  [getFilteredAssetKeyedByTwinId],
  filteredAssetKeyedByTwinId => [...filteredAssetKeyedByTwinId.keys()],
  stringArrayMemoizeOptions,
);

/**
 * The leaf layers the user is currently filtered to, derived purely from the
 * layer tree (no asset access). An explicit layer selection takes precedence:
 * the leaves come from the selected layers' subtrees (a "N/A"/unlocated-only
 * selection yields no leaves). Otherwise the opened layer is used. When there
 * is no layer filter at all (e.g. "all buildings"), every leaf in the tree is
 * returned. Output as layer paths via getLayerPathFromLayerID.
 */
export const getFilteredLeafLayerPaths = createSelector(
  [
    getAssetManagerFilters,
    map.layersKeyedById,
    getLayersKeyedByAncestorId,
    getLayersKeyedByParentID,
  ],
  (assetManagerFilters, layersKeyedById, layersKeyedByAncestorId, layersKeyedByParentId) => {
    if (!layersKeyedById) return [];
    const { openedLayerId, selectedLayerIds } = assetManagerFilters;

    const isLeaf = (id: string) => !layersKeyedByParentId.get(id)?.length;
    const allLeafPaths = () => [...layersKeyedById.keys()]
      .filter(isLeaf)
      .map(id => getLayerPathFromLayerID(layersKeyedById, id));

    const hasExplicitSelection = !!selectedLayerIds?.length;

    // No layer filter applied at all: every leaf in the tree.
    if (!hasExplicitSelection && !openedLayerId) return allLeafPaths();

    // A "N/A"/unlocated-only selection ([null]): every leaf in the tree.
    if (hasExplicitSelection && selectedLayerIds!.every(id => id === null)) return allLeafPaths();

    // An explicit selection wins over the opened layer.
    const scopeLayerIds = hasExplicitSelection
      ? selectedLayerIds
      : [openedLayerId as string];

    // Each scope layer expands to itself plus its entire subtree.
    const candidateIds = getSelectedLayerAncestors(scopeLayerIds, layersKeyedByAncestorId);

    return [...candidateIds]
      .filter(isLeaf)
      .map(id => getLayerPathFromLayerID(layersKeyedById, id));
  },
  stringArrayMemoizeOptions,
);

export const getTwinIDModelIdMap = createSelector(
  [map.models],
  (models) => {
    const output: { [TwinID: string]: string } = {};
    if (!models) return [];
    models.forEach((model) => {
      const { mapping } = model;
      Object.keys(mapping!).forEach((twinID) => {
        output[twinID] = model.id || '';
      });
    });
    return output;
  },
);

/**
 * Layer-path analog of getTwinIDModelIdMap, derived from each model's
 * isolationMapping (a layerPath -> dbIds map, built server-side by serverless-components).
 * Produces layerPath -> modelId so models can be resolved from an asset's layer
 * rather than its twinID. Empty when no model has an isolationMapping.
 */
export const getLayerPathModelIdMap = createSelector(
  [map.models, getSelectedAssetType],
  (models, selectedAssetType) => {
    const output: { [layerPath: string]: string } = {};
    if (!models) return output;
    const assetTypeId = selectedAssetType?.id;
    models
      .filter(model => model.assetTypes?.includes(assetTypeId))
      .forEach((model) => {
        const { isolationMapping } = model;
        if (!isolationMapping) return;
        Object.keys(isolationMapping).forEach((layerPath) => {
          output[layerPath] = model.id || '';
        });
      });
    return output;
  },
);

/**
 * Whether any model enabled for the currently selected asset type has an
 * isolationMapping. Gates the layer-path based model loading in
 * getFilteredModelIds.
 */
export const getCurrentAssetTypeHasIsolationMapping = createSelector(
  [map.models, getSelectedAssetType],
  (models, selectedAssetType) => {
    if (!models) return false;
    const assetTypeId = selectedAssetType?.id;
    return models.some(model => (
      !!model.assetTypes?.includes(assetTypeId)
      && !!model.isolationMapping
      && Object.keys(model.isolationMapping).length > 0
    ));
  },
);

export const getModelsKeyedById = createSelector(
  [map.models],
  models => keyBy(models, 'id'),
);

export const getMappedFilteredTwinIds = createSelector(
  [getFilteredAssetsForDigitalTwin, getTwinIDModelIdMap],
  (filteredAssets, twinIDModelIdMap) => filteredAssets
    .map(asset => asset?.destination?.twinID?.[0])
    .filter((twinID): twinID is string => !!twinID && twinID in twinIDModelIdMap),
);

export const getFilteredModelIds = createSelector(
  [
    getTwinIDModelIdMap,
    getLayerPathModelIdMap,
    getFilteredAssetsForTwin,
    map.layersKeyedById,
    getAssetManagerFilters,
    getCurrentAssetTypeHasIsolationMapping,
  ],
  (
    twinIDModelIdMap,
    layerPathModelIdMap,
    assets,
    layersKeyedById,
    assetManagerFilters,
    currentAssetTypeHasIsolationMapping,
  ) => {
    const modelIdsSet = new Set<string>();

    // When a model of the current asset type has an isolationMapping, drive
    // model loading off the currently opened layer (layerPath -> modelId)
    // instead of asset twinIDs.
    if (currentAssetTypeHasIsolationMapping) {
      const { openedLayerId } = assetManagerFilters;
      const openedLayerPath = openedLayerId && layersKeyedById
        ? getLayerPathFromLayerID(layersKeyedById, openedLayerId)
        : undefined;
      // No opened layer (e.g. "all buildings") loads every mapped model;
      // otherwise load models for any mapped layer under the opened layer path.
      const isUnderOpenedLayer = (layerPath: string) => !openedLayerPath
        || layerPath === openedLayerPath
        || layerPath.startsWith(`${openedLayerPath} > `);
      Object.entries(layerPathModelIdMap).forEach(([layerPath, modelId]) => {
        if (isUnderOpenedLayer(layerPath)) modelIdsSet.add(modelId);
      });
      return modelIdsSet.size ? [...modelIdsSet] : undefined;
    }

    assets.forEach((asset) => {
      const twinID = asset?.destination?.twinID?.[0];
      if (!twinID || !twinIDModelIdMap[twinID]) return;
      modelIdsSet.add(twinIDModelIdMap[twinID]);
    });
    return modelIdsSet.size ? [...modelIdsSet] : undefined;
  },
);

export const getFilteredAssetsKeyedByStatusForDigitalTwin = createSelector(
  [getAssetTypes, getFilteredAssetsForDigitalTwin],
  (assetTypes, filteredAssets) => {
    const assetKeyedByStatuses = new Map();

    if (assetTypes && filteredAssets) {
      const statusesArray = assetTypes.map(assetType => assetType.statuses!).flat();

      statusesArray?.forEach(({ id }) => {
        const assetsFilteredByStatusId = filteredAssets.filter(({ status }) => status === id);
        assetKeyedByStatuses.set(id, assetsFilteredByStatusId);
      });
    }

    return assetKeyedByStatuses as Map<string, Array<Asset>>;
  },
);

export const getSelectedAssetsKeyedByStatusForDigitalTwin = createSelector(
  [getAssetTypes, getFilteredAssetsForDigitalTwin],
  (assetTypes, filteredAssets) => {
    const assetKeyedByStatuses = new Map();

    if (assetTypes && filteredAssets) {
      const statusesArray = assetTypes.map(assetType => assetType.statuses!).flat();

      statusesArray?.forEach(({ id }) => {
        const assetsFilteredByStatusId = filteredAssets.filter(({ status }) => status === id);
        assetKeyedByStatuses.set(id, assetsFilteredByStatusId);
      });
    }

    return assetKeyedByStatuses as Map<string, Array<Asset>>;
  },
);

export const getUnMappedAsset = createSelector(
  [
    map.assetsKeyedById,
    getAssetManagerAssetSelection,
    getTwinIDModelIdMap,
  ],
  (assetsKeyedById, assetManagerAssetSelection, twinIDModelIdMap) => {
    const { selectedAssets } = assetManagerAssetSelection;
    return selectedAssets?.filter((id) => {
      const currentAsset = assetsKeyedById.get(id);
      const twinID = currentAsset?.destination?.twinID?.[0] || null;
      return !twinID || !twinIDModelIdMap[twinID];
    });
  },
);
